import React from "react";
import type { IExamReport } from "@/apis/exam_papers/getExamReport";
import type { ICategory } from "@/apis/categories/types";
import ProgressBar from "./ProgressBar";
import { QuestionTable } from "@/components/exam/QuestionTable";
import { CategoryStatsTable } from "@/components/exam/CategoryStatsTable";
import { Badge } from "@/components/badge";
import { FEATURES } from "@/config/features";
import { getPaperTypeLabel } from "@/constants/exam";

interface SATScoreReportProps {
  examReport: IExamReport;
  categories?: ICategory[];
}

const SATScoreReport: React.FC<SATScoreReportProps> = ({
  examReport,
  categories,
}) => {
  const isDevelopment = false; //process.env.NODE_ENV === "development";

  // Helper function to get section/module label based on exam type
  const getSectionLabel = (isSAT: boolean = false) => {
    return isSAT ? "Module" : "Section";
  };

  const calculateDomainPercentage = (
    domainQuestions: number,
    allDomains: Array<{ total_questions?: number }>
  ) => {
    const totalQuestions = allDomains.reduce(
      (sum, d) => sum + (d.total_questions || 0),
      0
    );
    return Math.round((domainQuestions / (totalQuestions || 1)) * 100);
  };

  const { subjects } = examReport;
  if (!subjects || subjects.length === 0) {
    return <div>No subject details found</div>;
  }

  // Check if it's SAT exam
  const isSAT = examReport?.exam_set?.exam_type === "SAT";

  return (
    <>
      {/* PAGE 1: SAT Practice Score Report */}
      <div
        className={`page w-[210mm] h-[297mm] px-8 py-6 text-black font-sans text-base bg-white ${
          isDevelopment ? "border-2 border-red-500" : ""
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-2 h-[100px]">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold tracking-tight ">
              S&S ACADEMY
            </span>
          </div>
          <div className="text-right ">
            <div className="inline-block rounded-lg p-2 text-left">
              <div className="text-lg mb-1 leading-none">
                <span className="inline-block w-24">Name:</span>
                <span className="font-bold">{examReport?.user?.name}</span>
              </div>
              {examReport?.group?.name && (
                <div className="text-base mb-1 leading-none">
                  <span className="inline-block w-24">Class:</span>
                  <span className="font-bold">{examReport?.group?.name}</span>
                </div>
              )}
              {examReport?.user?.school && (
                <div className="text-base mb-1 leading-none">
                  <span className="inline-block w-24">School:</span>
                  <span className="font-bold">{examReport?.user?.school}</span>
                </div>
              )}
              {examReport?.user?.grade && (
                <div className="text-base mb-1 leading-none">
                  <span className="inline-block w-24">Grade:</span>
                  <span className="font-bold">{examReport?.user?.grade}</span>
                </div>
              )}
              <div className="text-base mb-1 leading-none">
                <span className="inline-block w-24">Email:</span>
                <span className="font-bold">{examReport?.user?.email}</span>
              </div>
              <div className="text-base leading-none">
                <span className="inline-block w-24">Tested on:</span>
                <span className="font-bold">
                  {new Date(examReport?.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl font-extrabold mt-16 mb-6">
          Your SAT Score Report
        </h1>

        {/* Card Section */}
        <div className="rounded-xl border border-gray-300 bg-white shadow-sm mt-2 mb-4 p-0 overflow-hidden">
          <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 bg-gray-200">
            <div className="text-xl font-bold">
              {examReport?.exam_set?.title}
            </div>
            <div className="text-base text-gray-700">
              {new Date(examReport?.created_at).toLocaleDateString()}
            </div>
          </div>
          <div className="grid grid-cols-20 gap-0 divide-x divide-gray-200">
            {/* Total Score */}
            <div className="col-span-5 p-4 px-6 flex flex-col items-start">
              <div className="text-md font-extrabold">TOTAL SCORE</div>
              <div className="text-6xl font-bold leading-none mb-2">
                {examReport?.total_score}
              </div>
              <div className="text-sm text-gray-600 mb-2">
                Score Range
                <br /> 400-1600
              </div>
              <div className="text-sm text-gray-800 font-bold mt-2">
                Score Range:
              </div>
              <div className="text-sm text-gray-600">
                This range reflects the minimum and maximum total score you can
                get with this assessment.
              </div>
            </div>
            {/* Section Scores */}
            <div className="col-span-5 p-4 flex flex-col items-start">
              <div className="text-md font-extrabold mb-3">SECTION SCORES</div>
              {subjects.map((subjectReport, index) => (
                <React.Fragment key={subjectReport.subject}>
                  {index > 0 && (
                    <hr className="border-dashed border-gray-300 w-full my-2" />
                  )}
                  <div className="w-full flex-1">
                    <div className="font-bold text-sm mb-3">
                      {getPaperTypeLabel(subjectReport.subject)}
                    </div>
                    <div className="font-bold text-5xl leading-none">
                      {subjectReport.total_score || 0}
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
            {/* Score Details */}
            <div className="col-span-10 py-4 px-4 flex flex-col items-start">
              <div className="text-md font-extrabold mb-3">SCORE DETAILS</div>
              <div className="flex flex-col w-full text-sm h-full">
                {subjects.map((subjectReport, index) => {
                  const totalCorrect = subjectReport.sections.reduce(
                    (sum, s) =>
                      sum +
                      (s.questions?.filter((q) => q.is_correct).length || 0),
                    0
                  );
                  const totalQuestions = subjectReport.sections.reduce(
                    (sum, s) => sum + (s.questions?.length || 0),
                    0
                  );
                  const totalIncorrect = totalQuestions - totalCorrect;

                  return (
                    <React.Fragment key={subjectReport.subject}>
                      {index > 0 && (
                        <hr className="border-dashed border-gray-300 w-full my-4" />
                      )}
                      <div className="flex-1">
                        <div className="grid grid-cols-5 gap-2">
                          {/* Combined Results */}
                          <div className="col-span-2">
                            <p className="text-sm font-bold mb-2">Total</p>
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <p className="text-3xl font-bold">
                                  {totalCorrect}
                                </p>
                                <div className="border-l border-gray-300 h-6 mx-0"></div>
                                <p className="text-xs leading-none font-bold text-start">
                                  correct
                                  <br />
                                  Answers
                                </p>
                              </div>
                              <div className="mt-2">
                                <p className="text-xs leading-none">
                                  Total:{" "}
                                  <span className="font-normal">
                                    {totalQuestions}
                                  </span>
                                </p>
                                <p className="text-xs leading-none">
                                  Incorrect:{" "}
                                  <span className="font-normal">
                                    {totalIncorrect}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>
                          {/* Individual Sections */}
                          <div className="flex flex-col gap-2 col-span-3">
                            {subjectReport.sections.map((section, sIdx) => (
                              <div key={sIdx}>
                                <p className="text-sm font-bold mb-1">
                                  {getSectionLabel(isSAT)} {sIdx + 1}
                                </p>
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-2">
                                    <p className="text-3xl font-bold">
                                      {section.questions?.filter(
                                        (q) => q.is_correct
                                      ).length || 0}
                                    </p>
                                    <div className="border-l border-gray-300 h-6"></div>
                                    <p className="text-xs leading-none font-bold text-start">
                                      correct
                                      <br />
                                      Answers
                                    </p>
                                  </div>
                                  <div className="text-left">
                                    <p className="text-xs leading-none">
                                      Total:{" "}
                                      <span className="font-normal">
                                        {section.questions?.length || 0}
                                      </span>
                                    </p>
                                    <p className="text-xs leading-none">
                                      Incorrect:{" "}
                                      <span className="font-normal">
                                        {section.questions?.filter(
                                          (q) => !q.is_correct
                                        ).length || 0}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Knowledge and Skills */}
        <div className="mt-8">
          <div className="font-bold text-xl mb-1">KNOWLEDGE AND SKILLS</div>
          <div className="text-sm text-gray-700 mb-6">
            View your performance across the content domains measured on the SAT
          </div>
          <div className="grid grid-cols-2">
            {subjects.map((subjectReport) =>
              subjectReport.domains && subjectReport.domains.length > 0 ? (
                <div key={subjectReport.subject}>
                  <div className="flex items-center gap-3">
                    <div className="text-black font-semibold text-lg">
                      {getPaperTypeLabel(subjectReport.subject)}
                    </div>
                    {FEATURES.SHOW_REPORT_DIFFICULTY_LEVEL &&
                      subjectReport.difficulty && (
                        <Badge color="zinc">
                          Level {subjectReport.difficulty}
                        </Badge>
                      )}
                  </div>
                  {subjectReport.domains.map((domain) => (
                    <div key={domain?.id}>
                      <div className="mb-2 text-sm text-gray-700 mt-2">
                        <p className="font-bold">{domain.name}</p>
                        <p className="text-gray-600">
                          (
                          {calculateDomainPercentage(
                            domain?.total_questions || 0,
                            subjectReport.domains
                          )}
                          % of test section, {domain?.total_questions || 0}{" "}
                          questions)
                        </p>
                      </div>
                      <ProgressBar percentage={(domain?.accuracy || 0) * 100} />
                    </div>
                  ))}
                </div>
              ) : null
            )}
          </div>
        </div>
      </div>

      {/* Question Breakdown Page - all subjects on one page */}
      <div
        className={`page w-[210mm] h-[297mm] px-10 text-black font-sans text-sm bg-white ${
          isDevelopment ? "border-2 border-red-500" : ""
        }`}
        style={{ pageBreakBefore: "always" }}
      >
        {subjects.map((subjectReport) => {
          const isMath = subjectReport.subject === "math";
          return (
            <div key={subjectReport.subject} className="mb-4">
              {/* Question Tables - sections side by side */}
              <div className="grid grid-cols-2 grid-rows-1 gap-4">
                {subjectReport.sections.map((section, sIdx) => (
                  <div key={sIdx}>
                    <QuestionTable
                      title={`${getPaperTypeLabel(subjectReport.subject)} ${getSectionLabel(isSAT)} ${sIdx + 1}`}
                      questions={section.questions}
                      showAccuracy={true}
                      progressId={examReport?.exam_progress_id}
                    />
                  </div>
                ))}
              </div>

              {/* Category Stats - only for non-Math subjects */}
              {!isMath && (
                <div className="mt-4">
                  <CategoryStatsTable
                    title="QUESTION COUNTS BY DETAILED CATEGORIES"
                    examType={examReport?.exam_set?.exam_type || "SAT"}
                    subject={subjectReport.subject}
                    categoryStats={subjectReport.category_stats}
                    recentExamsStats={subjectReport.recent_exams_stats}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default SATScoreReport;
